import {
    useEffect, useLayoutEffect, useRef, useState,
} from 'react';

/**
 * When placed inside of a relative positioned element, children can be dragged around.
 *
 * clampToParent will prevent dragging outside of the parent element
 * @param {{
 * initial: {x: number, y: number} | 'topRight' | 'bottomLeft' | 'bottomRight' | 'topLeft',
 * clampToParent: boolean,
 * children: any
 * }} param0
 *
 * This component was made with AI assistance from ChatGPT
 */
export function DraggableContainer({
    initial = { x: 0, y: 0 },
    clampToParent = true,
    children,
    scale = 1,
    ...rest
}) {
    const elRef = useRef(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    useLayoutEffect(() => {
        const el = elRef.current;
        if (!el) return;

        const parent = el.offsetParent;
        if (!parent) return;

        const parentRect = parent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        let x = 0;
        let y = 0;

        if (typeof initial === 'string') {
            switch (initial) {
                case 'bottomRight':
                    x = parentRect.width - elRect.width;
                    y = parentRect.height - elRect.height;
                    break;
                case 'bottomLeft':
                    y = parentRect.height - elRect.height;
                    break;
                case 'topRight':
                    x = parentRect.width - elRect.width;
                    break;
                case 'topLeft':
                default:
            }
        } else {
            x = initial.x ?? 0;
            y = initial.y ?? 0;
        }

        if (clampToParent) {
            const pr = parent.getBoundingClientRect();
            const r = el.getBoundingClientRect();
            x = Math.max(0, Math.min(x, pr.width - r.width));
            y = Math.max(0, Math.min(y, pr.height - r.height));
        }

        setPos({ x, y });
    }, [initial, clampToParent]);

    // move if parent resizes
    useEffect(() => {
        if (!clampToParent) return undefined;

        const el = elRef.current;
        if (!el) return undefined;

        const parent = el.offsetParent;
        if (!parent) return undefined;

        const setPosition = () => {
            const pr = parent.getBoundingClientRect();
            const r = el.getBoundingClientRect();
            setPos((p) => ({
                x: Math.max(0, Math.min(p.x, pr.width - r.width)),
                y: Math.max(0, Math.min(p.y, pr.height - r.height)),
            }));
        };

        const ro = new ResizeObserver(() => {
            setPosition();
        });

        ro.observe(parent);

        return () => {
            ro.disconnect();
        };
    }, [clampToParent, scale]);

    const onPointerDown = (e) => {
        const el = elRef.current;
        if (!el) return;

        const parent = el.offsetParent;
        if (!parent) return;

        el.setPointerCapture(e.pointerId);

        const parentRect = parent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const grabX = e.clientX - elRect.left;
        const grabY = e.clientY - elRect.top;

        const onMove = (ev) => {
            let x = ev.clientX - parentRect.left - grabX;
            let y = ev.clientY - parentRect.top - grabY;

            if (clampToParent) {
                x = Math.max(0, Math.min(x, parentRect.width - elRect.width));
                y = Math.max(0, Math.min(y, parentRect.height - elRect.height));
            }

            setPos({ x, y });
        };

        const onUp = (ev) => {
            el.releasePointerCapture(ev.pointerId);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    return (
        <div
            ref={elRef}
            onPointerDown={onPointerDown}
            {...rest}
            style={{
                position: 'absolute',
                left: pos.x,
                top: pos.y,
                cursor: 'grab',
                touchAction: 'none',
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                ...rest.style,
            }}
        >
            {children}
        </div>
    );
}
